// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; captions are visual labels for the adjacent control (same convention as SupplierFormModal / the UserAdmin* lists, Task #25).
//
// =============================================================================
// SchoolFormModal — New / Edit school as an Unleashed-style modal
// =============================================================================
// Shared modal for creating a school and editing an existing row. Layout follows
// the CLAUDE.md "Form / field layout" convention: two columns, right-aligned
// labels, fixed-width inputs sized to the data (not stretched). Cancel/Save sit
// bottom-left (app convention). Reads/writes go through src/data/schools.
//
// ACARA ID is the CSV-import dedup key (partial-unique in the DB, db-56); it is
// OPTIONAL for a manual add (a brand-new school may not have one yet). A duplicate
// ACARA ID is rejected by the unique index and surfaced as a save error. The
// deactivate/reactivate toggle lives on the list, not here.
// =============================================================================
import React from "react";
import { createSchools, updateSchoolById } from "../../data/schools";
import { alertDialog } from "../../services/dialogService";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";
import { SchoolByodManager } from "./SchoolByodManager.jsx";

const STATES = ["", "NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
// Standard ACARA school types; the current value is merged in on edit so an
// unexpected stored value is never silently dropped.
const TYPE_OPTIONS = ["", "Primary", "Secondary", "Combined", "Special"];

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "4px 8px",
  borderRadius: 6,
  border: "1px solid #CFD8DC",
  fontSize: 13,
  color: "#1A2E2B",
  background: "#fff",
  fontFamily: "inherit",
};

function Row({ label, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "96px 200px",
        gap: 8,
        alignItems: "center",
        marginBottom: 6,
      }}
    >
      <label style={{ textAlign: "right", fontSize: 12, color: "#5A6B68" }}>{label}</label>
      <div>{children}</div>
    </div>
  );
}

function Txt({ value, onChange, placeholder, maxLength, width }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      style={width ? { ...inputStyle, width } : inputStyle}
    />
  );
}

function Sel({ value, onChange, options, ariaLabel }) {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o === "" ? "—" : o}
        </option>
      ))}
    </select>
  );
}

function seedForm(mode, row) {
  const r = row || {};
  return {
    name: mode === "edit" ? r.name || "" : "",
    acara_id: r.acara_id || "",
    suburb: r.suburb || "",
    state: r.state || "",
    postcode: r.postcode || "",
    school_type: r.school_type || "",
    school_sl: !!r.school_sl,
  };
}

// mode: "create" | "edit". row: the school row (edit) or null (create).
// onClose(): dismiss. onSaved(): parent refreshes its list.
export function SchoolFormModal({ mode, row, onClose, onSaved }) {
  const [form, setForm] = React.useState(() => seedForm(mode, row));
  const [saving, setSaving] = React.useState(false);
  const setF = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Merge an unexpected stored school_type into the options so edit never drops it.
  const typeOptions = TYPE_OPTIONS.includes(form.school_type)
    ? TYPE_OPTIONS
    : [...TYPE_OPTIONS, form.school_type];

  async function handleSave() {
    const trimOrNull = (v) => (v || "").trim() || null;
    const name = (form.name || "").trim();
    if (!name) {
      await alertDialog({ title: "Name required", message: "Enter a school name." });
      return;
    }
    const patch = {
      name,
      acara_id: trimOrNull(form.acara_id),
      suburb: trimOrNull(form.suburb),
      state: trimOrNull(form.state),
      postcode: trimOrNull(form.postcode),
      school_type: trimOrNull(form.school_type),
      school_sl: !!form.school_sl,
    };

    setSaving(true);
    let error;
    if (mode === "edit" && row) {
      ({ error } = await updateSchoolById(row.id, patch));
    } else {
      ({ error } = await createSchools([{ ...patch, deactivated: false }]));
    }
    setSaving(false);
    if (error) {
      // The most likely failure is a duplicate ACARA ID (partial-unique index).
      const dup = /duplicate|unique/i.test(error.message || "");
      await alertDialog({
        title: "Save failed",
        message: dup ? `A school with ACARA ID "${patch.acara_id}" already exists.` : error.message,
      });
      return;
    }
    logAudit(mode === "edit" ? "school_updated" : "school_created", {
      id: mode === "edit" && row ? row.id : undefined,
      ...patch,
    });
    onSaved();
    onClose();
  }

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      contentStyle={{ maxWidth: 640 }}
      draggable
      dragTitle={mode === "edit" ? `Edit School — ${form.name}` : "Add School"}
    >
      <div style={{ background: "#fff", borderRadius: 10, padding: "12px 18px" }}>
        <h2
          style={{
            margin: "0 0 8px",
            paddingBottom: 8,
            borderBottom: "1px solid #E0E7E6",
            fontSize: 18,
            color: "#1A2E2B",
          }}
        >
          {mode === "edit" ? "Edit School" : "Add School"}
        </h2>

        {/* Two columns of fixed-width fields (CLAUDE.md form-layout rules 1-5) */}
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
          <div>
            <Row label="Name">
              <Txt value={form.name} onChange={(v) => setF("name", v)} placeholder="School name" />
            </Row>
            <Row label="Suburb">
              <Txt value={form.suburb} onChange={(v) => setF("suburb", v)} />
            </Row>
            <Row label="Type">
              <Sel
                value={form.school_type}
                onChange={(v) => setF("school_type", v)}
                options={typeOptions}
                ariaLabel="School type"
              />
            </Row>
          </div>

          <div>
            <Row label="ACARA ID">
              <Txt
                value={form.acara_id}
                onChange={(v) => setF("acara_id", v)}
                placeholder="(optional)"
                maxLength={16}
                width={120}
              />
            </Row>
            <Row label="State">
              <Sel
                value={form.state}
                onChange={(v) => setF("state", v)}
                options={STATES}
                ariaLabel="State"
              />
            </Row>
            <Row label="Postcode">
              <Txt
                value={form.postcode}
                onChange={(v) => setF("postcode", v)}
                maxLength={8}
                width={80}
              />
            </Row>
            <Row label="School Locker">
              <label
                style={{
                  fontSize: 13,
                  color: "#1A2E2B",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.school_sl}
                  onChange={(e) => setF("school_sl", e.target.checked)}
                />
                School Locker school
              </label>
            </Row>
          </div>
        </div>

        {/* School-level BYOD band manager (edit mode only — a new school has no id yet) */}
        {mode === "edit" && row && <SchoolByodManager school={row} />}

        {/* Cancel / Save bottom-left (app convention) */}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <Btn variant="secondary" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Btn>
        </div>
      </div>
    </ModalBackdrop>
  );
}
